import { ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BrainCircuit,
  CalendarClock,
  DatabaseBackup,
  FileText,
  FlaskConical,
  HeartPulse,
  LayoutDashboard,
  LineChart,
  LogOut,
  Menu,
  Moon,
  NotebookPen,
  Package,
  Pill,
  Settings,
  Search,
  ShieldCheck,
  Sun,
  Target,
  Upload,
  Users,
  Wrench,
  Zap,
  BookOpen,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { PwaControls } from "@/components/pwa/PwaControls";
import { WorkspaceSwitch } from "@/components/layout/WorkspaceSwitch";
import { cn } from "@/lib/utils";

type NavItem = { to: string; label: string; icon: any };
type NavGroup = { label: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Painel e atendimento",
    items: [
      { to: "/", label: "Painel clínico", icon: LayoutDashboard },
      { to: "/pacientes", label: "Paciente", icon: Users },
      { to: "/sessoes", label: "Consultas e sessões", icon: CalendarClock },
    ],
  },
  {
    label: "Prontuário e evolução",
    items: [
      { to: "/journal", label: "Linha do tempo", icon: NotebookPen },
      { to: "/diario-paciente", label: "Diário clínico", icon: FileText },
      { to: "/sintomas", label: "Sintomas-alvo", icon: Target },
      { to: "/relatorios", label: "Relatórios clínicos", icon: BarChart3 },
    ],
  },
  {
    label: "Tratamento e segurança",
    items: [
      { to: "/medicamentos", label: "Tratamento", icon: Pill },
      { to: "/doses", label: "Doses e exposições", icon: Activity },
      { to: "/uso-substancias", label: "Uso de substâncias", icon: HeartPulse },
      { to: "/interacoes", label: "Interações", icon: Zap },
      { to: "/estoque-medicamentos", label: "Estoque de medicamentos", icon: Package },
    ],
  },
  {
    label: "Análise longitudinal",
    items: [
      { to: "/inteligencia-clinica", label: "Inteligência clínica", icon: BrainCircuit },
      { to: "/insights", label: "Insights longitudinais", icon: BarChart3 },
      { to: "/meu-espaco", label: "Sinais vitais e check-in", icon: HeartPulse },
      { to: "/curvas", label: "Curvas temporais", icon: LineChart },
    ],
  },
  {
    label: "Farmacologia e conhecimento",
    items: [
      { to: "/laboratorio-pkpd", label: "Laboratório PK/PD", icon: FlaskConical },
      { to: "/biblioteca", label: "Biblioteca farmacológica", icon: BookOpen },
      { to: "/conhecimento-farmacologico", label: "Base de conhecimento", icon: Upload },
      { to: "/base-farmacologica/workbench", label: "Editor avançado", icon: Wrench },
    ],
  },
  {
    label: "Administração do sistema",
    items: [
      { to: "/auditoria", label: "Auditoria clínica", icon: ShieldCheck },
      { to: "/backup", label: "Backup e restauração", icon: DatabaseBackup },
      { to: "/importar", label: "Importação de dados", icon: Upload },
      { to: "/diagnostico-sistema", label: "Diagnóstico do sistema", icon: Activity },
      { to: "/configuracoes", label: "Configurações", icon: Settings },
      { to: "/ferramentas", label: "Central de ferramentas", icon: Wrench },
    ],
  },
];

const BOTTOM_NAV = [
  { to: "/", label: "Painel", icon: LayoutDashboard },
  { to: "/journal", label: "Timeline", icon: NotebookPen },
  { to: "/doses", label: "Registros", icon: Activity },
  { to: "/curvas", label: "PK/PD", icon: FlaskConical },
  { to: "/inteligencia-clinica", label: "Clínico", icon: BrainCircuit },
];

function useTheme() {
  const [theme, setTheme] = useState<"light" | "dark">(
    () => (localStorage.getItem("pn-theme") as any)
      || "dark",
  );
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("pn-theme", theme);
  }, [theme]);
  return { theme, toggle: () => setTheme((current) => (current === "dark" ? "light" : "dark")) };
}

function Brand() {
  return (
    <div className="px-4 py-4 border-b border-sidebar-border">
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-lg bg-sidebar-primary text-sidebar-primary-foreground grid place-items-center"><Activity className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-tight">PsicoNorte Clinical</div>
          <div className="text-[11px] text-sidebar-foreground/55">ClinicalOS</div>
        </div>
      </div>
    </div>
  );
}

function NavList({
  pathname,
  onNav,
  query,
  onQueryChange,
}: {
  pathname: string;
  onNav?: () => void;
  query: string;
  onQueryChange: (value: string) => void;
}) {
  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const currentGroup = NAV_GROUPS.find((group) => group.items.some((item) =>
    item.to === "/" ? pathname === "/" : pathname.startsWith(item.to),
  ))?.label;
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() =>
    Array.from(new Set(["Painel e atendimento", currentGroup].filter(Boolean) as string[])),
  );

  useEffect(() => {
    setExpandedGroups(Array.from(new Set(
      ["Painel e atendimento", currentGroup].filter(Boolean) as string[],
    )));
  }, [currentGroup]);

  const filteredGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (!normalizedQuery) return true;
      return item.label.toLocaleLowerCase("pt-BR").includes(normalizedQuery)
        || group.label.toLocaleLowerCase("pt-BR").includes(normalizedQuery);
    }),
  })).filter((group) => group.items.length > 0);

  function toggleGroup(groupLabel: string, isOpen: boolean) {
    setExpandedGroups((current) => isOpen
      ? (current.includes(groupLabel) ? current : [...current, groupLabel])
      : current.filter((label) => label !== groupLabel));
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="px-3 pt-3 pb-1">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/45" />
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Buscar ferramenta…"
            aria-label="Buscar no menu"
            className="h-10 w-full rounded-xl border border-sidebar-border bg-sidebar-accent/35 pl-9 pr-3 text-sm text-sidebar-foreground placeholder:text-sidebar-foreground/40 outline-none transition-colors focus:border-sidebar-ring focus:ring-2 focus:ring-sidebar-ring/20"
          />
        </label>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-2" aria-label="Navegação principal">
        {filteredGroups.length ? filteredGroups.map((group) => (
          <details
            key={group.label}
            open={normalizedQuery.length > 0 || expandedGroups.includes(group.label)}
            onToggle={(event) => {
              if (!normalizedQuery) toggleGroup(group.label, event.currentTarget.open);
            }}
            className="group/nav rounded-xl"
          >
            <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between gap-2 rounded-xl px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.11em] text-sidebar-foreground/55 transition-colors hover:bg-sidebar-accent/35 hover:text-sidebar-foreground [&::-webkit-details-marker]:hidden">
              <span>{group.label}</span>
              <span className="flex items-center gap-2">
                <span className="rounded-md bg-sidebar-accent/60 px-1.5 py-0.5 text-[9px] tabular-nums">{group.items.length}</span>
                <span className="text-xs transition-transform group-open/nav:rotate-180" aria-hidden="true">⌄</span>
              </span>
            </summary>
            <div className="space-y-1 pb-2 pt-1">
              {group.items.map((item) => {
                const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onNav}
                    className={cn(
                      "flex min-h-10 items-center gap-3 rounded-xl px-3 py-2 text-[13px] transition-colors",
                      active
                        ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm ring-1 ring-sidebar-border"
                        : "text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                    )}
                  >
                    <item.icon className="h-[17px] w-[17px] shrink-0 opacity-90" />
                    <span className="min-w-0 flex-1 leading-snug">{item.label}</span>
                    {active ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sidebar-primary" aria-hidden="true" /> : null}
                  </NavLink>
                );
              })}
            </div>
          </details>
        )) : (
          <div className="px-4 py-8 text-center text-sm text-sidebar-foreground/55">
            Nenhuma ferramenta corresponde a “{query}”.
          </div>
        )}
      </nav>
    </div>
  );
}

function mobileTitle(pathname: string) {
  if (pathname.startsWith("/journal")) return "Journal";
  if (pathname.startsWith("/biblioteca")) return "Library";
  if (pathname.startsWith("/ferramentas")) return "Tools";
  if (pathname.startsWith("/insights")) return "Insights";
  if (pathname.startsWith("/meu-espaco")) return "Sinais vitais e check-in";
  if (pathname.startsWith("/laboratorio-pkpd")) return "Laboratório PK/PD";
  if (pathname.startsWith("/inteligencia-clinica")) return "Clínico";
  if (pathname.startsWith("/backup")) return "Backup e restauração";
  if (pathname.startsWith("/pacientes")) return "Paciente";
  if (pathname.startsWith("/sessoes")) return "Consultas e sessões";
  if (pathname.startsWith("/doses")) return "Doses e exposições";
  if (pathname.startsWith("/interacoes")) return "Interações";
  if (pathname.startsWith("/sintomas")) return "Sintomas-alvo";
  if (pathname.startsWith("/relatorios")) return "Relatórios clínicos";
  if (pathname.startsWith("/medicamentos")) return "Tratamento";
  if (pathname.startsWith("/curvas")) return "PK/PD · Curvas";
  return "ClinicalOS";
}

function SidebarWorkspace() {
  return (
    <div className="border-b border-sidebar-border px-3 py-3">
      <div className="rounded-xl bg-sidebar-accent/40 p-1">
        <WorkspaceSwitch />
      </div>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navQuery, setNavQuery] = useState("");

  return (
    <div className="piru-workspace min-h-[100dvh] flex w-full overflow-x-hidden bg-background text-foreground">
      <aside className="hidden md:flex sticky top-0 h-[100dvh] w-56 xl:w-60 shrink-0 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
        <Brand />
        <SidebarWorkspace />
        <NavList pathname={pathname} query={navQuery} onQueryChange={setNavQuery} />
        <div className="px-3 py-3 border-t border-sidebar-border text-xs text-sidebar-foreground/65 space-y-2">
          <PwaControls />
          <div className="truncate px-2">{user?.email}</div>
          <Button size="sm" variant="ghost" className="w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent" onClick={signOut}>
            <LogOut className="h-4 w-4 mr-2" />Sair
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 max-w-full">
        <header className="min-h-14 pt-[env(safe-area-inset-top)] border-b bg-card/85 backdrop-blur-2xl sticky top-0 z-30 flex items-end px-2.5 sm:px-4 gap-2">
          <div className="h-14 w-full flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden shrink-0 rounded-full" aria-label="Abrir menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[min(84vw,320px)] bg-sidebar text-sidebar-foreground border-sidebar-border pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
                <div className="flex flex-col h-full">
                  <Brand />
                  <SidebarWorkspace />
                  <NavList pathname={pathname} query={navQuery} onQueryChange={setNavQuery} onNav={() => { setMobileOpen(false); setNavQuery(""); }} />
                  <div className="px-3 py-3 border-t border-sidebar-border text-xs text-sidebar-foreground/65 space-y-2">
                    <PwaControls />
                    <div className="truncate px-2">{user?.email}</div>
                    <Button size="sm" variant="ghost" className="w-full justify-start" onClick={signOut}>
                      <LogOut className="h-4 w-4 mr-2" />Sair
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <div className="md:hidden min-w-0">
              <div className="font-semibold text-sm truncate">{mobileTitle(pathname)}</div>
              <div className="text-[10px] text-muted-foreground truncate">PsicoNorte Clinical</div>
            </div>
            <div className="hidden md:block w-[190px]">
              <WorkspaceSwitch compact />
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <PwaControls compact />
              <Button variant="ghost" size="icon" className="rounded-full" onClick={toggle} aria-label="Alternar tema">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </header>

        <main className="clinical-main flex-1 min-w-0 max-w-full overflow-x-hidden p-4 sm:p-5 md:p-6 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-8">
          <div className="workspace-content">{children}</div>
        </main>

        <div className="pointer-events-none md:hidden fixed left-0 right-0 bottom-0 z-40 bg-card/95 border-t px-[max(0.45rem,env(safe-area-inset-left))] pr-[max(0.45rem,env(safe-area-inset-right))] pb-[env(safe-area-inset-bottom)] backdrop-blur-2xl">
          <nav aria-label="Navegação clínica principal" className="pointer-events-auto mx-auto flex h-16 w-full max-w-xl min-w-0 items-stretch px-1">
            {BOTTOM_NAV.map((item) => {
              const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "relative flex min-w-0 flex-1 basis-0 flex-col items-center justify-center gap-1 overflow-hidden rounded-xl px-0.5 text-[clamp(9px,2.6vw,10px)] leading-none transition-colors",
                    active ? "text-primary font-semibold" : "text-muted-foreground",
                  )}
                >
                  <item.icon className={cn("h-5 w-5", active && "stroke-[2.3]")} />
                  <span className="block w-full truncate text-center max-w-full">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
}
